import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  CSSProperties,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { themeColors, themeTypography } from 'src/theme';
import {
  ResolvedBranding,
  brandingToCssVars,
  resolveBranding,
} from './branding';
import './brandFonts.css';
import './CircleBrandingScope.css';

const ScopedBrandingContext = createContext<ResolvedBranding>(
  resolveBranding(null)
);

/**
 * The branding of the circle a component is rendered in. Outside a branded
 * circle (and in every unbranded circle) this is the unbranded default.
 */
export const useScopedBranding = (): ResolvedBranding =>
  useContext(ScopedBrandingContext);

interface CircleBrandingScopeProps {
  branding: ResolvedBranding;
  children: ReactNode;
}

/**
 * Applies a circle's branding to its subtree only: CSS custom properties on a
 * `display: contents` wrapper (so no box is added to the layout) plus a scoped
 * MUI theme. When the circle is not branded the children are returned as-is,
 * which keeps every existing circle pixel-identical.
 *
 * Dialogs, menus and tooltips opened from inside the circle are rendered into
 * the same wrapper instead of `document.body`, so they pick up the brand too
 * while pop-ups that belong to the app (top bar, sidebar) stay Dunbar.
 */
export const CircleBrandingScope: FC<CircleBrandingScopeProps> = ({
  branding,
  children,
}) => {
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);

  // The browser tab carries the circle's own name while the circle is open
  const brandTitle = branding.hasDisplayName ? branding.displayName : null;
  useEffect(() => {
    if (!brandTitle) {
      return undefined;
    }
    const previousTitle = document.title;
    document.title = brandTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [brandTitle]);

  const theme = useMemo(() => {
    if (!branding.isBranded) {
      return null;
    }
    const portalTarget = wrapper ? { container: wrapper } : {};
    return createTheme({
      palette: {
        ...themeColors,
        ...(branding.hasAccent && {
          primary: {
            main: branding.accentColor,
            contrastText: branding.onAccentColor,
          },
        }),
      },
      typography: {
        ...themeTypography,
        ...(branding.hasFont && { fontFamily: branding.fontFamily }),
      },
      components: {
        MuiModal: { defaultProps: portalTarget },
        MuiPopover: { defaultProps: portalTarget },
        MuiPopper: { defaultProps: portalTarget },
        MuiButton: {
          styleOverrides: {
            // Pill buttons are the "website" style: fully rounded, sentence case
            ...(branding.buttonShape === 'pill' && {
              root: { borderRadius: 9999, textTransform: 'none' as const },
            }),
            // A bright accent fills buttons well but is hard to read as text
            ...(branding.hasAccent && {
              outlinedPrimary: {
                color: branding.accentTextColor,
                borderColor: branding.accentTextColor,
              },
              textPrimary: { color: branding.accentTextColor },
            }),
          },
        },
      },
    });
  }, [branding, wrapper]);

  if (!branding.isBranded || !theme) {
    return <>{children}</>;
  }

  // display: contents adds no box, but inherited properties such as the font
  // still flow down to every descendant of the circle page.
  const style = {
    display: 'contents',
    ...(branding.hasFont && { fontFamily: branding.fontFamily }),
    ...brandingToCssVars(branding),
  } as CSSProperties;

  return (
    <div
      ref={setWrapper}
      data-circle-branding="true"
      data-brand-font={branding.hasFont ? 'true' : undefined}
      data-brand-header={branding.hasHeaderSurface ? 'true' : undefined}
      data-brand-page={branding.hasPageSurface ? 'true' : undefined}
      data-brand-heading-font={branding.hasHeadingFont ? 'true' : undefined}
      data-brand-heading-upper={branding.headingUppercase ? 'true' : undefined}
      style={style}
    >
      <ThemeProvider theme={theme}>
        <ScopedBrandingContext.Provider value={branding}>
          {children}
        </ScopedBrandingContext.Provider>
      </ThemeProvider>
    </div>
  );
};
